import { completeTransferIn } from "@/api/transactions";

// A card the iframe charged but transfer_in/complete hasn't settled yet. Kept so a refresh, a
// closed tab or a lost connection doesn't leave the charge without its credit: the app finishes
// it on its next load (useResumePendingTransferIn). Past the intent's expiry the API's
// crons/kashu_settle_pending_transfer_ins settles it instead.
const STORAGE_KEY = "kashu_pending_transfer_in";

export type PendingTransferIn = {
	guestId: number;
	cardId: string;
	locationId: number;
	transferIntent: string;
	transactionId: string;
	expiresAt: string;
};

export function savePendingTransferIn(pending: PendingTransferIn) {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
	} catch {
		// Storage unavailable: the API cron still settles the charge.
	}
}

export function loadPendingTransferIn(): PendingTransferIn | null {
	try {
		const saved = localStorage.getItem(STORAGE_KEY);
		return saved ? (JSON.parse(saved) as PendingTransferIn) : null;
	} catch {
		return null;
	}
}

export function clearPendingTransferIn(transactionId?: string) {
	try {
		if (
			transactionId &&
			loadPendingTransferIn()?.transactionId !== transactionId
		) {
			return;
		}
		localStorage.removeItem(STORAGE_KEY);
	} catch {
		// Nothing to clear.
	}
}

// Wait between attempts when transfer_in/complete can't be reached or isn't ready yet.
const COMPLETE_RETRY_DELAYS_MS = [2_000, 5_000, 10_000, 20_000];

// No answer (network error / timeout), the transaction isn't final yet (409) or a server error:
// worth retrying, complete is idempotent. Any other answer is the outcome.
export function isRetryableCompleteError(error: unknown) {
	const status = (error as { http_status?: number } | null)?.http_status;
	return (
		status === undefined ||
		status === 0 ||
		status === 409 ||
		status === 429 ||
		status >= 500
	);
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * transfer_in/complete, retried while it can't get an answer. Keeps the pending transfer
 * stored until the API gives an outcome (success or a final error).
 */
export async function completePendingTransferIn(pending: PendingTransferIn) {
	savePendingTransferIn(pending);
	for (let attempt = 0; ; attempt++) {
		try {
			const result = await completeTransferIn({
				guestId: pending.guestId,
				cardId: pending.cardId,
				locationId: pending.locationId,
				transferIntent: pending.transferIntent,
				transactionId: pending.transactionId,
			});
			clearPendingTransferIn(pending.transactionId);
			return result;
		} catch (error) {
			if (
				!isRetryableCompleteError(error) ||
				attempt >= COMPLETE_RETRY_DELAYS_MS.length
			) {
				if (!isRetryableCompleteError(error)) {
					clearPendingTransferIn(pending.transactionId);
				}
				throw error;
			}
			await wait(COMPLETE_RETRY_DELAYS_MS[attempt]);
		}
	}
}
